import { Grid, Theme, Tooltip, Typography, Button } from "@mui/material";
import {
  IAircraft,
  IFlightAircraft,
  IFlightFlag,
  IFlightTemplate,
  IFlightType,
  IFullAircraftConfiguration,
  INewTemplateFlight,
  PolicyResource,
} from "@vmosolutions/oc-interfaces";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { isEqual } from "lodash";
import moment from "moment-timezone";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { FormikSelectField, FormikTextField, FormikTimeField } from "src/components/FormikComponents/FormikWrappers";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { formatTimeString } from "src/components/FormikComponents/TimeField";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAirportOption, IStructuresState } from "src/redux/reducers/StructuresReducer";
import { formatIdentFromAirport, updateAirportOptions } from "src/utils/airportHelper";
import { isEpochDate, milli2DaysHrsMin, validateDateTimeFormat } from "src/utils/index";
import { v1 } from "uuid";
import { EventTypes } from "../FlightBuilder";

interface IFlightRowEditProps {
  initialValues: INewTemplateFlight;
  legIndex: number;
  legCount: number;
  mosaicPane: HTMLElement;
  onSubmit: (flightValues: INewTemplateFlight) => void;
  theme: Theme;
  eventType?: EventTypes;
  buildAsLocal: boolean;
  setEditRow: React.Dispatch<React.SetStateAction<number>>;
  startDate: number;
  focusField?: string;
  addNewLeg: (p: boolean) => void;
}

export const newFlightTemplate: IFlightTemplate = {
  // TEMPLATE SHARED VALUES
  fileName: undefined,
  isLeafNode: true,
  path: [],
  rRule: undefined,
  operator: undefined,
  pairingPositionCounts: undefined,
  legs: [],
  asLocal: true,
};

export const defaultFlightValues: INewTemplateFlight = {
  _id: undefined,
  flightNumber: undefined,
  aircraft: undefined,
  origin: undefined,
  destination: undefined,
  dayOffset: 0,
  scheduledOut: "00:00",
  scheduledIn: "00:00",
  customer: undefined,
  flightType: undefined,
  aircraftConfiguration: undefined,
  note: undefined,
  comment: undefined,
  flags: [],
};

export const getInitialValues = (defaultFlightType?: IFlightType): INewTemplateFlight => {
  return { ...defaultFlightValues, _id: `temp-${v1()}`, flightType: defaultFlightType ?? undefined };
};

type NewFlightForm = FormikProps<INewTemplateFlight>;

/**
 * Function which renders the FlightFlag (saved as a string), into it's SVG Element
 * @param svgString - SVG string saved in the data.
 * @returns - SVG Element rendered on screen.
 */
export const renderFlagIcon = (svgString: string): JSX.Element => {
  if (svgString) {
    return <div dangerouslySetInnerHTML={{ __html: svgString }} />;
  }
  return null;
};

/**
 * Return block time in d+HH:MM format.
 * @param scheduledOut - epoch
 * @param scheduledIn - epoch
 * @returns string
 */
export const getBlockDurationString = (
  scheduledOut: number | string,
  scheduledIn: number | string,
  isUTC = true,
  originTZ?: string,
  destinationTZ?: string
): string => {
  if (scheduledIn && scheduledOut) {
    // Epoch values are used when editing normal flights.
    if (isEpochDate(scheduledIn) && isEpochDate(scheduledOut)) {
      return milli2DaysHrsMin((scheduledIn as number) - (scheduledOut as number), true);
    } else if (
      typeof scheduledIn === "string" &&
      scheduledIn.search(":") > -1 &&
      typeof scheduledOut === "string" &&
      scheduledOut.search(":") > -1
    ) {
      //if recurring and there are no destination/origin - return HH:mm
      if ((!destinationTZ || !originTZ) && !isUTC) {
        return "No TZ"; //"d+HH:mm";
      }
      // Recurring flights used Hrs:mm strings so use moment to calculate diff.
      const startTime = isUTC
        ? moment(scheduledOut, "HH:mm")
        : moment.tz(moment(scheduledOut, "HH:mm").format("YYYY-MM-DD HH:mm:ss"), originTZ);
      const endTime = isUTC
        ? moment(scheduledIn, "HH:mm")
        : moment.tz(moment(scheduledIn, "HH:mm").format("YYYY-MM-DD HH:mm:ss"), destinationTZ);
      // If arrival hrs are smaller than departure, assume arrival is next day (add 24 hours).
      if (endTime.hours() < startTime.hours()) {
        endTime.add(24, "hours");
      }
      // calculate total duration
      const duration = moment.duration(endTime.diff(startTime));
      return milli2DaysHrsMin(duration.asMilliseconds(), true);
    }
  }
  return "d+HH:mm";
};

////////////////////////////
// FORM Flight Validation //
////////////////////////////
export const validateFlight = (values: INewTemplateFlight): any => {
  const {
    flightNumber,
    origin,
    destination,
    dayOffset,
    scheduledOut,
    scheduledIn,
    flightType,
    note,
    aircraft,
    aircraftConfiguration,
  } = values;

  // nested values have to be added here as empty objects.
  const errors: any = {};

  if (!flightNumber && flightNumber !== 0) {
    errors.flightNumber = "Required";
  }
  if (flightNumber && flightNumber.toString().length > 5) {
    errors.flightNumber = "< 5 Digits";
  }
  if (!origin) {
    errors.origin = "Required";
  }
  if (!destination) {
    errors.destination = "Required";
  }
  if (!scheduledOut) {
    errors.scheduledOut = "Required";
  }
  if (!scheduledIn) {
    errors.scheduledIn = "Required";
  }
  if (!flightType) {
    errors.flightType = "Required";
  }

  // Validate Date/Time values.
  const dateTimeArry = ["scheduledOut", "scheduledIn"];
  for (const unTypedField of dateTimeArry) {
    const field = unTypedField as keyof typeof values;
    // Display any inline errors triggered by the component when validating the date and time inputs.
    // If value is a string, allow it since it's the time component passed in from recurring flights
    if (typeof values[field] !== "string" && values[field] && !!validateDateTimeFormat(values[field]).errorString) {
      errors[field] = validateDateTimeFormat(values[field]).errorString;
    } else if (typeof values[field] === "string" && !!formatTimeString(values[field])?.error) {
      // If format passed in is a string, assuming it's a "time" string passed in HH:mm format and validate it.
      errors[field] = formatTimeString(values[field])?.error;
    }
  }

  // Validate small note size
  if (!!note && note.length > 100) {
    errors.note = "< 100 Letters";
  }

  // Check if scheduled arrival is after departure.
  if (!errors.scheduledOut && !errors.scheduledIn && !!scheduledIn && !!scheduledOut) {
    if (new Date(scheduledIn) <= new Date(scheduledOut)) {
      errors.scheduledOut = "After Arrival";
      errors.scheduledIn = "Before Departure";
    }
  }

  // Check that the aircraft has a configuration
  if (aircraft && !aircraftConfiguration) {
    errors.aircraftConfiguration = "Missing Config";
  }
  return errors;
};

/**
 * Using the assigned time string, return a UTC time epoch
 * This is required if we want to be able to allow more user friendly toggling of time strings in the input fields.
 */
export const returnDateTimeEpoch = (time: string, startDate: number, dayOffset?: number): number => {
  const offset = dayOffset ?? 0;
  if (!time) {
    return moment.utc(startDate).add(offset, "days").valueOf();
  }
  const dateToCreate = moment.utc(startDate).add(offset, "days");
  const [year, month, date] = [dateToCreate.year(), dateToCreate.month(), dateToCreate.date()];
  const [hours, minutes] = time.split(":").map((val: string) => parseInt(val));
  return moment(Date.UTC(year, month, date, hours, minutes)).valueOf();
};

/**
 * Given a Datetime in epoch format, return only the assigned time.
 */
export const returnOnlyTime = (date: number): string => {
  return !date ? "00:00" : moment.utc(date).format("HH:mm");
};

/**
 * Show selected Flight Row in Edit Mode
 * @param props -
 */
export const FlightRowEdit = (props: IFlightRowEditProps): JSX.Element => {
  const {
    initialValues,
    legIndex,
    legCount,
    mosaicPane,
    onSubmit,
    theme,
    eventType,
    buildAsLocal,
    setEditRow,
    startDate,
    focusField,
    addNewLeg,
  } = props;
  const dispatch = useDispatch();
  const [showMoreRow, setShowMoreRow] = useState<boolean>(false);

  useLayoutEffect(() => {
    // On mount, auto focus the flight number field.
    setTimeout(() => {
      const field = focusField ?? "flightNumber_input";
      document.getElementById(field)?.focus();
    }, 50);
  }, [focusField]);

  // Retrieve Field Permission access check.
  const hasPermissions = useSelector((state: IRootState) => state.authentication.hasPermissions);

  // Structure dependencies
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions, customerStructure } = structures;
  const { flightTypes, customers, flightFlags } = customerStructure;

  const getTimezone = useCallback(
    (location: string) => {
      return airportOptions?.[location]?.timezoneId;
    },
    [airportOptions]
  );

  const flightEventTypes = useMemo(() => {
    return flightTypes?.length ? flightTypes.filter((type: IFlightType) => type.isFlight) : [];
  }, [flightTypes]);
  const aircraftOptions = customerStructure?.aircraft;

  const [aircraftConfigOptions, setAircraftConfigOptions] = useState<IFullAircraftConfiguration[]>([]);
  const getAircraftConfigurations = useCallback(async (aicraftId: string): Promise<IFullAircraftConfiguration[]> => {
    const response = await AmplifyAPIWrapper.Get<IFullAircraftConfiguration[]>(
      `/aircraftConfiguration?aircraftId=${aicraftId}`
    );
    return Array.isArray(response) ? response : [];
  }, []);

  useEffect(() => {
    const updateConfig = async (): Promise<void> => {
      if (initialValues?.aircraft?._id) {
        const aircraftConfigOptions = await getAircraftConfigurations(initialValues?.aircraft?._id);
        setAircraftConfigOptions(aircraftConfigOptions);
      }
    };
    updateConfig();
  }, [initialValues, getAircraftConfigurations]);

  const getDateTimeMoment = useCallback(
    (time: string, location: string, dayOffset: number, departureMoment?: moment.Moment): moment.Moment | null => {
      if (!startDate || !time) {
        return null;
      }
      const offset = dayOffset ?? 0;

      const dateToCreate = moment.utc(startDate).add(offset, "days");
      const [year, month, date] = [dateToCreate.year(), dateToCreate.month(), dateToCreate.date()];
      const [hours, minutes] = time.split(":").map((val: string) => parseInt(val));
      const tz = !buildAsLocal || !location ? "Etc/UTC" : getTimezone(location);
      // console.log(moment.tz([year, month, date, hours, minutes], tz));
      const dateObject = moment.tz([year, month, date, hours, minutes], tz);
      // if departure is after arrival, assume overnight flight and add a day.
      return departureMoment && departureMoment > dateObject ? dateObject.add(1, "days") : dateObject;
    },
    [getTimezone, startDate, buildAsLocal]
  );

  // Assign default configuration based on selected aircraft (only called onChange);
  const assignDefaultConfiguration = useCallback(
    async (newAC: IAircraft, flightForm: NewFlightForm): Promise<void> => {
      const aircraftConfigOptions = await getAircraftConfigurations(newAC?._id); // Get configuration options for this aircraft.
      setAircraftConfigOptions(aircraftConfigOptions);
      const { setFieldValue, setFieldTouched } = flightForm;
      const ACConfig =
        newAC && aircraftOptions?.length > 0
          ? aircraftOptions.find((full) => full._id === newAC._id)?.defaultConfig
          : null;
      setFieldValue("aircraftConfiguration", ACConfig);
      setFieldTouched("aircraftConfiguration", true);
    },
    [aircraftOptions, getAircraftConfigurations]
  );

  /** PASS Flight values up to flight builder component */
  const handleSubmit = useCallback(
    (values: INewTemplateFlight, helpers?: FormikHelpers<INewTemplateFlight>): void => {
      onSubmit(values);
    },
    [onSubmit]
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      // validationSchema={FlightTypeSchema} // Look into replacing validation with Yup.
      validate={validateFlight}
    >
      {(flightForm: NewFlightForm) => {
        const { initialValues, values, dirty, isValid, isSubmitting, setFieldValue, setFieldTouched } = flightForm;
        // const formIsDefault = isEqual({ ...defaultFlightValues, _id: undefined }, { ...values, _id: undefined });

        // If values have changed and are valid, autosubmit form to update flight.
        if (!isEqual(initialValues, values)) {
          const valid = validateFlight(values);
          if (Object.entries(valid)?.length === 0) {
            handleSubmit(values);
          }
        }

        const validateAndCreateNewRow = (event: React.KeyboardEvent): void => {
          const focusedField = document?.activeElement?.id;
          const lastRow = legIndex + 1 === legCount;
          if (
            isValid &&
            ((event?.code === "Enter" && event?.shiftKey) ||
              (event?.code === "Tab" && focusedField === "aircraftConfiguration"))
          ) {
            if (lastRow) addNewLeg(true);
            else addNewLeg(false);
          }
        };

        const flightTypeColor = values?.flightType?.color
          ? `#${values?.flightType?.color}`
          : theme.custom.forms.inputBack;
        const contrastText = theme.palette.getContrastText(flightTypeColor);

        // Returns true if time string of In time if smaller than out time (indicating an overnight flight)
        const originTZ = !!airportOptions && values?.origin ? airportOptions[values.origin]?.timezoneId : "Etc/UTC";
        const destinationTZ =
          !!airportOptions && values?.destination ? airportOptions[values.destination]?.timezoneId : "Etc/UTC";

        const departureMoment = getDateTimeMoment(values?.scheduledOut, values?.origin, values?.dayOffset);
        const departureIsDST = departureMoment?.isDST && departureMoment?.isDST();
        const arrivalMoment = getDateTimeMoment(
          values?.scheduledIn,
          values?.destination,
          values?.dayOffset,
          departureMoment
        );
        const arrivalIsDST = arrivalMoment?.isDST && arrivalMoment?.isDST();

        const blockDuration = (): string => {
          if (!departureMoment || !arrivalMoment) {
            return "d+HH:mm";
          }
          const duration = moment.duration(arrivalMoment.diff(departureMoment));
          return milli2DaysHrsMin(duration.asMilliseconds(), true);
        };

        const departureTooltip = buildAsLocal
          ? `${departureMoment?.format("MMM Do, Y HH:mm z")} ${departureIsDST ? "(in DST)" : ""}`
          : departureMoment?.utc()?.format("MMM Do, Y HH:mm z");

        const arrivalTooltip = buildAsLocal
          ? `${arrivalMoment?.format("MMM Do, Y HH:mm z")} ${arrivalIsDST ? "(in DST)" : ""}`
          : arrivalMoment?.utc()?.format("MMM Do, Y HH:mm z");

        return (
          <Form className={"form_styles"}>
            <Grid container direction="row" className="flight_row" onKeyDown={validateAndCreateNewRow}>
              <Grid container direction="row" flexWrap="nowrap" sx={{ width: "auto" }}>
                <Grid item className="checkbox">
                  <CheckboxField checked={true} onChange={() => setEditRow(-1)} />
                </Grid>
                <Grid item className="flight_leg">
                  <Typography variant="caption">{(legIndex + 1).toString()}</Typography>
                </Grid>
                <Grid
                  container
                  direction="row"
                  className={`required_fields ${isValid ? "" : "invalid"}`}
                  flexWrap="nowrap"
                >
                  <FormikTextField
                    style={{ backgroundColor: flightTypeColor, color: contrastText }}
                    className={`flight_number ${values?.flightType?.color ? "type_assigned" : ""}`}
                    label=""
                    name="flightNumber"
                    disabled={!hasPermissions(PolicyResource.Flight, "flightNumber")}
                  />
                  <Tooltip placement="top" title={`UTC ${departureMoment?.tz(originTZ)?.utcOffset()} minutes`}>
                    <Grid item>
                      <FormikSelectField
                        //style={{ backgroundColor: flightTypeColor, color: contrastText }}
                        className={"origin"}
                        label=""
                        name="origin"
                        format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                        parse={(airport: IAirportOption) => airport?.ident}
                        onInputChange={(ident: string) => {
                          updateAirportOptions(ident, airportOptions, dispatch);
                        }}
                        options={Object.values(airportOptions)}
                        // For search purposes, include ICAO, IATA, and AIRPORT NAME.
                        // IATA is omitted when absent so the literal "null" never enters the search text.
                        getOptionLabel={(airport: IAirportOption) =>
                          `${airport.ident} - ${airport.iata ? `${airport.iata} - ` : ""}${airport.name}`
                        }
                        // Display ICAO only.
                        formatOptionLabel={(airport: IAirportOption) => `${airport.ident}`}
                        getOptionValue={(airport: IAirportOption) => airport.ident}
                        isSearchable={true}
                        menuPortalTarget={mosaicPane}
                        noOptionsMessage={() => "Search for airport name or code"}
                        isMulti={false}
                        menuPosition={"fixed"}
                        openMenuOnFocus={false}
                        isClearable={false}
                        placeholder=""
                        disabled={!hasPermissions(PolicyResource.Flight, "origin")}
                      />
                    </Grid>
                  </Tooltip>

                  <Tooltip placement="top" title={`UTC ${arrivalMoment?.tz(destinationTZ)?.utcOffset()} minutes`}>
                    <Grid item>
                      <FormikSelectField
                        //style={{ backgroundColor: flightTypeColor, color: contrastText }}
                        className={"destination"}
                        label=""
                        name="destination"
                        format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                        parse={(airport: IAirportOption) => airport?.ident}
                        onInputChange={(ident: string) => {
                          updateAirportOptions(ident, airportOptions, dispatch);
                        }}
                        options={Object.values(airportOptions)}
                        // For search purposes, include ICAO, IATA, and AIRPORT NAME.
                        // IATA is omitted when absent so the literal "null" never enters the search text.
                        getOptionLabel={(airport: IAirportOption) =>
                          `${airport.ident} - ${airport.iata ? `${airport.iata} - ` : ""}${airport.name}`
                        }
                        // Display ICAO only.
                        formatOptionLabel={(airport: IAirportOption) => `${airport.ident}`}
                        getOptionValue={(airport: IAirportOption) => airport.ident}
                        isSearchable={true}
                        menuPortalTarget={mosaicPane}
                        noOptionsMessage={() => "Search for airport name or code"}
                        isMulti={false}
                        menuPosition={"fixed"}
                        openMenuOnFocus={false}
                        isClearable={false}
                        placeholder=""
                        disabled={!hasPermissions(PolicyResource.Flight, "destination")}
                      />
                    </Grid>
                  </Tooltip>

                  <FormikSelectField
                    className="flight_type"
                    name="flightType"
                    label=""
                    isMulti={false}
                    options={flightEventTypes.sort((a, b) => {
                      return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
                    })}
                    getOptionLabel={(type: IFlightType) => `${type.title} - ${type.description}`}
                    getOptionValue={(flightType: IFlightType) => flightType._id}
                    placeholder=""
                    isSearchable={true}
                    menuPortalTarget={mosaicPane}
                    menuPosition={"fixed"}
                    disabled={!hasPermissions(PolicyResource.Flight, "flightType")}
                  />

                  <FormikTextField
                    className="day_offset"
                    label=""
                    name="dayOffset"
                    type="number"
                    inputProps={{ min: "0", max: "365" }}
                    onChange={(offset) => {
                      if (offset <= 0 || null) {
                        setFieldValue("dayOffset", 0);
                      } else if (offset > 99) {
                        setFieldValue("dayOffset", 99);
                      }
                    }}
                  />
                  <Tooltip placement="top" title={departureTooltip}>
                    <Grid item>
                      <FormikTimeField
                        className={`scheduled_out ${buildAsLocal ? "local" : ""}`}
                        label=""
                        name="scheduledOut"
                      />
                    </Grid>
                  </Tooltip>
                  <Tooltip placement="top" title={arrivalTooltip}>
                    <Grid item>
                      <FormikTimeField
                        className={`scheduled_in ${buildAsLocal ? "local" : ""}`}
                        label=""
                        name="scheduledIn"
                      />
                    </Grid>
                  </Tooltip>

                  <Typography className="block_duration" variant="caption">
                    {blockDuration()}
                  </Typography>
                </Grid>

                <Grid container direction="row" className="optional_fields" flexWrap="nowrap">
                  <FormikSelectField
                    className="aircraft"
                    name="aircraft"
                    label=""
                    isMulti={false}
                    options={aircraftOptions}
                    onChange={(ac) => assignDefaultConfiguration(ac, flightForm)}
                    // For search purposes, include registration, aircraftType, aircraftType title.
                    getOptionLabel={(aircraft: IFlightAircraft) =>
                      `${aircraft.registration} (${aircraft.aircraftType && aircraft.aircraftType.title})`
                    }
                    // Display registration only.
                    formatOptionLabel={(aircraft: IFlightAircraft) => `${aircraft.registration}`}
                    getOptionValue={(aircraft: IFlightAircraft) => aircraft?._id}
                    placeholder=""
                    menuPortalTarget={mosaicPane}
                    menuPosition={"fixed"}
                    isSearchable={true}
                    isClearable={true}
                    disabled={!hasPermissions(PolicyResource.Flight, "aircraft")}
                  />

                  <FormikSelectField
                    className="aircraft_config"
                    name="aircraftConfiguration"
                    label=""
                    isMulti={false}
                    options={aircraftConfigOptions}
                    getOptionLabel={(option: any) => option.title}
                    getOptionValue={(option: any) => option._id}
                    placeholder=""
                    menuPortalTarget={mosaicPane}
                    menuPosition={"fixed"}
                    isSearchable={true}
                    isClearable={true}
                    disabled={!hasPermissions(PolicyResource.Flight, "aircraftConfiguration")}
                  />
                  <Button
                    onClick={() => setShowMoreRow((prev) => (prev ? false : true))}
                    color="info"
                    autoFocus={true}
                    sx={{ fontSize: "11px", padding: "2px 8px", margin: 0 }}
                  >
                    {showMoreRow ? "less..." : "more..."}
                  </Button>
                </Grid>
              </Grid>

              {showMoreRow && (
                <Grid container direction="row" className="more_optional_fields">
                  <FormikSelectField
                    className="flags"
                    name="flags"
                    label="Flags"
                    isMulti={true}
                    options={flightFlags.sort((a, b) => {
                      return a.description.toLowerCase().localeCompare(b.description.toLowerCase());
                    })}
                    parse={(option: any) => option ?? []}
                    getOptionLabel={(option: IFlightFlag) => {
                      return `${option.description} [ ${option.title} ]`;
                    }}
                    formatOptionLabel={(option: IFlightFlag) => {
                      return (
                        <Grid container alignItems="center" wrap="nowrap">
                          <Grid item className="flag_icon" style={{ width: "auto" }}>
                            {renderFlagIcon(option?.icon)}
                          </Grid>
                          <Grid
                            item
                            style={{ marginLeft: 5, width: "auto" }}
                          >{`${option.description} [ ${option.title} ]`}</Grid>
                        </Grid>
                      );
                    }}
                    getOptionValue={(option: IFlightFlag) => option._id}
                    placeholder=""
                    menuPortalTarget={mosaicPane}
                    menuPosition={"fixed"}
                    isSearchable={true}
                    isClearable={true}
                    disabled={!hasPermissions(PolicyResource.Flight, "flags")}
                  />

                  <FormikSelectField
                    className="customer"
                    name="customer"
                    label="Customer"
                    isMulti={false}
                    options={customers.sort((a, b) => {
                      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                    })}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    placeholder=""
                    menuPortalTarget={mosaicPane}
                    menuPosition={"fixed"}
                    isSearchable={true}
                    isClearable={true}
                    disabled={!hasPermissions(PolicyResource.Flight, "customer")}
                  />

                  <FormikTextField
                    className="formik_field textfield multiline_long note"
                    label="Small Note"
                    name="note"
                    multiline={true}
                    minRows={2}
                    maxRows={5}
                    disabled={!hasPermissions(PolicyResource.Flight, "note")}
                  />

                  <FormikTextField
                    className="formik_field textfield multiline_long comment"
                    label="Comment"
                    name="comment"
                    multiline={true}
                    minRows={2}
                    maxRows={5}
                    disabled={!hasPermissions(PolicyResource.Flight, "comment")}
                  />
                </Grid>
              )}
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
